import { useCallback, useEffect, useRef, useState } from "react";

const UNITY = 80;
const gainFor = (m, anySolo) => ((anySolo ? m.solo : !m.mute) ? m.volume / UNITY : 0);

/** Plays every part of a split song together (one <audio> per part, Web Audio gains for level / mute / solo). */
export default function useStemPlayer(setId, stems) {
  const ctxRef = useRef(null);
  const els = useRef({});
  const gains = useRef({});
  const [mix, setMix] = useState(() => Object.fromEntries(stems.map((s) => [s.name, { volume: UNITY, mute: false, solo: false }])));
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const mixRef = useRef(mix);
  mixRef.current = mix;

  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const anySolo = Object.values(mixRef.current).some((m) => m.solo);
    stems.forEach((s) => {
      const a = new Audio();
      a.preload = "auto";
      a.src = s.mp3_url;
      const g = ctx.createGain();
      g.gain.value = gainFor(mixRef.current[s.name] || { volume: UNITY }, anySolo);
      ctx.createMediaElementSource(a).connect(g).connect(ctx.destination);
      els.current[s.name] = a;
      gains.current[s.name] = g;
    });
    const lead = els.current[stems[0]?.name];
    const onMeta = () => Number.isFinite(lead.duration) && setDuration(lead.duration);
    const onEnd = () => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); };
    lead?.addEventListener("loadedmetadata", onMeta);
    lead?.addEventListener("ended", onEnd);
    return () => {
      Object.values(els.current).forEach((a) => { a.pause(); a.removeAttribute("src"); a.load(); });
      els.current = {};
      gains.current = {};
      ctx.close();
    };
  }, [setId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const anySolo = Object.values(mix).some((m) => m.solo);
    Object.entries(mix).forEach(([name, m]) => { if (gains.current[name]) gains.current[name].gain.value = gainFor(m, anySolo); });
  }, [mix]);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      const all = Object.values(els.current);
      const now = all[0]?.currentTime || 0;
      setTime(now);
      all.slice(1).forEach((a) => { if (Math.abs(a.currentTime - now) > 0.08) a.currentTime = now; });
    }, 250);
    return () => clearInterval(t);
  }, [playing]);

  const play = useCallback(async () => {
    const all = Object.values(els.current);
    const at = all[0]?.currentTime || 0;
    ctxRef.current?.resume();
    all.forEach((a) => { a.currentTime = at; });
    try { await Promise.all(all.map((a) => a.play())); setPlaying(true); } catch { all.forEach((a) => a.pause()); setPlaying(false); }
  }, []);
  const pause = useCallback(() => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); }, []);
  const seek = useCallback((t) => { Object.values(els.current).forEach((a) => { a.currentTime = t; }); setTime(t); }, []);
  const update = useCallback((name, patch) => setMix((m) => ({ ...m, [name]: { ...m[name], ...patch } })), []);
  const reset = useCallback(() => setMix(Object.fromEntries(stems.map((s) => [s.name, { volume: UNITY, mute: false, solo: false }]))), [stems]);
  return { mix, playing, time, duration, play, pause, seek, update, reset };
}
